import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CameraCapture } from "@/components/chat/CameraCapture";
import { ImageEditor } from "@/components/chat/ImageEditor";
import { ImageLightbox } from "@/components/chat/ImageLightbox";
import { MessageSquare, Send, Camera, Image as ImageIcon } from "lucide-react";
import { showToast } from "@/components/shared/Toast";

interface RawComment {
  id: string;
  author: "admin" | "client";
  encrypted_text: string;
  nonce: string;
  is_image: boolean;
  created_at: string;
}

interface DecryptedComment {
  id: string;
  author: "admin" | "client";
  text: string;
  is_image: boolean;
  imageUrl?: string;
  created_at: string;
}

interface SessionCommentsProps {
  comments: RawComment[];
  currentRole: "admin" | "client";
  encryptValue: (value: string) => Promise<string>;
  decryptValue: (value: string) => Promise<string>;
  encryptBinary: (data: Uint8Array) => Promise<string | null>;
  decryptBinary: (encrypted: string) => Promise<Uint8Array | null>;
  hasKey: boolean;
  onSendText: (encrypted_text: string) => Promise<void>;
  onSendImage: (encrypted_text: string) => Promise<void>;
}

export function SessionComments({
  comments,
  currentRole,
  encryptValue,
  decryptValue,
  encryptBinary,
  decryptBinary,
  hasKey,
  onSendText,
  onSendImage,
}: SessionCommentsProps) {
  const [decrypted, setDecrypted] = useState<DecryptedComment[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedCanvas, setCapturedCanvas] = useState<HTMLCanvasElement | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Decrypt all comments on change
  useEffect(() => {
    if (!comments.length) {
      setDecrypted([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const results: DecryptedComment[] = [];
      for (const c of comments) {
        if (c.is_image) {
          const bytes = await decryptBinary(c.encrypted_text);
          let imageUrl: string | undefined;
          if (bytes) {
            const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "image/jpeg" });
            imageUrl = URL.createObjectURL(blob);
          }
          results.push({ id: c.id, author: c.author, text: "", is_image: true, imageUrl, created_at: c.created_at });
        } else {
          const text = await decryptValue(c.encrypted_text);
          results.push({ id: c.id, author: c.author, text, is_image: false, created_at: c.created_at });
        }
      }
      if (!cancelled) setDecrypted(results);
    })();
    return () => { cancelled = true; };
  }, [comments, decryptValue, decryptBinary]);

  // Scroll to bottom when comments change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [decrypted]);

  const handleSendText = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const encrypted = await encryptValue(text.trim());
      await onSendText(encrypted);
      setText("");
    } catch {
      showToast.error("Failed to send comment");
    } finally {
      setSending(false);
    }
  };

  const handleCapture = (canvas: HTMLCanvasElement) => {
    setShowCamera(false);
    setCapturedCanvas(canvas);
  };

  const handleEditorConfirm = async (bytes: Uint8Array) => {
    setCapturedCanvas(null);
    setSending(true);
    try {
      const encrypted = await encryptBinary(bytes);
      if (!encrypted) throw new Error("Encryption key not set");
      await onSendImage(encrypted);
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed to send image");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) +
      ", " + d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (!hasKey) return null;

  return (
    <>
      {showCamera && (
        <CameraCapture onCapture={handleCapture} onClose={() => setShowCamera(false)} />
      )}
      {capturedCanvas && (
        <ImageEditor
          sourceCanvas={capturedCanvas}
          onConfirm={handleEditorConfirm}
          onCancel={() => setCapturedCanvas(null)}
        />
      )}
      {lightboxUrl && (
        <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      )}

      <Card className="border-app-border rounded-2xl overflow-hidden pt-0">
        <CardHeader className="pb-2 bg-app-surface-raised/40 rounded-t-2xl pt-4">
          <CardTitle className="text-body flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-app-primary/15 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-app-primary" />
            </div>
            <span>Session Notes</span>
            {comments.length > 0 && (
              <span className="ml-auto text-info bg-app-surface-raised px-2 py-0.5 rounded-full text-app-text-muted">
                {comments.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-3 space-y-3">
          {/* Comment list */}
          {decrypted.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {decrypted.map((c) => {
                const isOwn = c.author === currentRole;
                return (
                  <div key={c.id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${
                      isOwn
                        ? "bg-app-primary text-white rounded-br-sm"
                        : "bg-app-surface-raised border border-app-border rounded-bl-sm"
                    }`}>
                      {c.is_image ? (
                        c.imageUrl ? (
                          <button type="button" onClick={() => setLightboxUrl(c.imageUrl!)}>
                            <img
                              src={c.imageUrl}
                              alt="Session image"
                              className="rounded-xl max-w-full max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            />
                          </button>
                        ) : (
                          <div className="flex items-center gap-2 text-app-text-muted py-1">
                            <ImageIcon className="w-4 h-4" />
                            <span className="text-info">[Image]</span>
                          </div>
                        )
                      ) : (
                        <p className="text-body whitespace-pre-wrap overflow-wrap-anywhere">{c.text}</p>
                      )}
                      <p className={`text-xs mt-1 opacity-60 ${isOwn ? "text-right" : ""}`}>
                        {formatTime(c.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}

          {decrypted.length === 0 && (
            <p className="text-info text-app-text-muted text-center py-3">No notes yet. Add the first one.</p>
          )}

          {/* Input row */}
          <div className="flex items-center gap-2 pt-1 border-t border-app-border">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-app-text-muted hover:text-app-primary"
              onClick={() => setShowCamera(true)}
              disabled={sending}
              title="Attach photo"
            >
              <Camera className="w-5 h-5" />
            </Button>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a note…"
              className="flex-1"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendText(); } }}
              disabled={sending}
            />
            <Button
              type="button"
              size="icon"
              className="shrink-0 bg-app-primary hover:bg-app-primary-hover"
              onClick={handleSendText}
              disabled={!text.trim() || sending}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
